import { createFileRoute } from "@tanstack/react-router";
import { fallback, zodValidator } from "@tanstack/zod-adapter";
import { z } from "zod";
import { useEffect, useState } from "react";
import { AppShell } from "@/components/layout/AppShell";
import { useSearch } from "@/lib/query-hooks";
import { SearchIcon, FileTextIcon } from "lucide-react";

const schema = z.object({
  q: fallback(z.string(), "").default(""),
});

export const Route = createFileRoute("/search")({
  validateSearch: zodValidator(schema),
  head: () => ({
    meta: [
      { title: "Search · PN-CivilServices" },
      { name: "description", content: "Search across every filed document." },
      { property: "og:title", content: "Search · PN-CivilServices" },
      { property: "og:description", content: "Search across every filed document." },
    ],
  }),
  component: SearchPage,
});

function SearchPage() {
  const { q } = Route.useSearch();
  const nav = Route.useNavigate();
  const [term, setTerm] = useState(q);
  const [debounced, setDebounced] = useState(q);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(term), 300);
    return () => clearTimeout(id);
  }, [term]);
  useEffect(() => {
    if (debounced !== q) nav({ search: { q: debounced }, replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  const { data, isFetching } = useSearch(debounced);
  const items = data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <AppShell>
      <div className="mx-auto max-w-3xl px-4 pt-12 pb-24">
        <div className="label-card">Search</div>
        <h1 className="mt-1 font-display text-3xl font-semibold">
          The whole ledger, indexed.
        </h1>

        <div className="mt-6 flex items-center gap-2 rounded-md border border-hairline bg-surface px-3 h-12 focus-within:ring-2 focus-within:ring-paprika">
          <SearchIcon className="h-5 w-5 text-ink-muted" />
          <input
            autoFocus
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Try: '2019 UPSC prelims GS', 'Laxmikanth Chapter 3'"
            className="w-full bg-transparent outline-none text-base placeholder:text-ink-muted"
          />
        </div>

        <div className="mt-8">
          {debounced.trim().length < 2 ? (
            <p className="text-ink-soft text-sm">
              Type at least 2 characters to search.
            </p>
          ) : isFetching && items.length === 0 ? (
            <p className="text-ink-muted text-sm">Searching…</p>
          ) : items.length === 0 ? (
            <p className="text-ink-soft text-sm">
              No matches. Try a different keyword or year.
            </p>
          ) : (
            <ul className="card-ledger divide-y divide-hairline overflow-hidden">
              {items.map((f) => (
                <li
                  key={f.id}
                  className="flex items-center gap-4 px-5 py-3 hover:bg-surface-alt/50"
                >
                  <FileTextIcon className="h-5 w-5 text-ink-soft" />
                  <div className="flex-1">
                    <div className="font-medium">{f.title ?? f.name}</div>
                    <div className="text-xs text-ink-muted">
                      {f.year ? `Year ${f.year} · ` : ""}
                      {(f.mimeType || "").split("/")[1]?.toUpperCase()}
                    </div>
                  </div>
                  {f.downloadUrl ? (
                    <a
                      href={f.downloadUrl}
                      className="text-xs text-paprika font-semibold uppercase tracking-widest"
                    >
                      Open
                    </a>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </AppShell>
  );
}
